import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, X, Eye, EyeOff, Upload, PieChart } from "lucide-react";
import { toast } from "sonner";
import { compare, thesisVisualData } from "@/lib/api";
import { getPortfolio, upsertPosition, removePosition, setPositionAlert, setAllPositionAlerts } from "@/lib/portfolio";
import { fmtPrice, fmtNum, fmtPctSigned, fmtPctRaw, ratioColor } from "@/lib/format";
import { computeCustomRatios } from "@/lib/customRatios";
import { sourceUnavailable } from "@/lib/dataSource";
import { useThresholds } from "@/lib/useThresholds";
import { useAuth } from "@/lib/auth";
import { useFx, useDonutFx } from "@/lib/fx";
import { useI18n } from "@/lib/i18n";
import AlertToggle from "@/components/AlertToggle";
import { alertInfoText } from "@/components/AlertInfoBanner";
import { LevelFilters, useLevelFilters, passesLevelFilters, levelFiltersActive } from "@/components/LevelFilters";
import MasterAlertToggle from "@/components/MasterAlertToggle";
import TickerAutocomplete from "@/components/TickerAutocomplete";
import { ViewToggle } from "@/components/ViewToggle";
import { CompanyCard } from "@/components/CompanyCard";
import { NextEarnings, nextEarningsInfo } from "@/components/NextEarnings";
import { CardSort } from "@/components/CardSort";
import { PortfolioDonut } from "@/components/PortfolioDonut";
import ConfirmDialog from "@/components/ConfirmDialog";
import { SortableTh, makeSorter, nextSort } from "@/components/SortableTh";
import { QUAL_TIPS } from "@/lib/qualTips";

const PF_NUMERIC_KEYS = new Set(["shares", "price", "mcap", "now", "pfpct", "rc", "rv", "score", "tam", "kpi"]);

// Options offered by the card-view sort dropdown (Nivel 1).
const PF_SORT_OPTIONS = [
    { key: "ticker", label: "Alfabético (Ticker)" },
    { key: "price", label: "Precio" },
    { key: "mcap", label: "Capitalización" },
    { key: "rc", label: "Ratio Compra" },
    { key: "rv", label: "Ratio Venta" },
    { key: "score", label: "Score" },
    { key: "tam", label: "TAM" },
    { key: "kpi", label: "Coef KPI" },
    { key: "earnings", label: "Próx. resultados" },
];

// Apply manual overrides to the API-fetched company snapshot before recomputing
// ratios. Mirrors the same logic the Watchlist page uses.
const enrichRow = (companyData, position) => {
    if (!companyData || companyData.error) return companyData;
    // «Nuestro» without official figures for the company: no ratio, like the ficha.
    if (sourceUnavailable(companyData)) return { ...companyData, custom_ratios: {} };
    const merged = {
        revenue_2y: companyData.auto_projections?.revenue_2y,
        fcf_2y: companyData.auto_projections?.fcf_2y,
        shares_outstanding: companyData.shares_outstanding,
        gross_margin: companyData.gross_margin,
        operating_margin: companyData.operating_margin,
        net_debt: companyData.net_debt,
        market_cap: companyData.market_cap,
        revenue_cagr_4y: companyData.auto_projections?.revenue_cagr_4y,
        fcf_cagr_4y: companyData.auto_projections?.fcf_cagr_4y,
        current_price: companyData.current_price,
    };
    if (position?.mode === "manual" && position?.overrides) {
        for (const [k, v] of Object.entries(position.overrides)) {
            if (k === "current_price") continue;
            merged[k] = v;
        }
    }
    const cr = computeCustomRatios(merged);
    return { ...companyData, custom_ratios: cr };
};

export default function Portfolio() {
    const { t } = useI18n();
    const { user } = useAuth();
    const { display: displayCur, convert: fxConvert } = useFx();
    const { donutCur, toDonut } = useDonutFx();
    const [positions, setPositions] = useState([]);
    const [rows, setRows] = useState([]);
    const [qual, setQual] = useState({});  // ticker → { score, tam, kpi_coef }
    const [loading, setLoading] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [editing, setEditing] = useState(null);
    const [sort, setSort] = useState(null);
    const [view, setView] = useState(() => localStorage.getItem("vs:portfolio-view") || "table");
    const changeView = (v) => { setView(v); localStorage.setItem("vs:portfolio-view", v); };
    // Default ON (hidden) the very first time — no stored preference yet — so a
    // fresh session doesn't show money amounts until the user opts in. Once they
    // toggle it, that explicit choice ("0" = shown) persists across navigation.
    const [hideMoney, setHideMoney] = useState(() => localStorage.getItem("vs:portfolio-hide-money") !== "0");
    const toggleHideMoney = () => setHideMoney((v) => { const n = !v; localStorage.setItem("vs:portfolio-hide-money", n ? "1" : "0"); return n; });
    const moneyCls = hideMoney ? "blur-sm select-none" : "";
    useThresholds();
    const donutRef = useRef(null);
    const scrollToDonuts = () => donutRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    // Qualitative layer (score / TAM / coef KPI) from the user's theses, by ticker.
    useEffect(() => {
        thesisVisualData()
            .then((d) => {
                const m = {};
                (d.rows || []).forEach((r) => { m[r.ticker] = { score: r.avg_overall_score, tam: r.sum_tam_score, kpi_coef: r.kpi_coef }; });
                setQual(m);
            })
            .catch(() => setQual({}));
    }, []);

    const load = async (poss) => {
        if (!poss.length) { setRows([]); return; }
        setLoading(true);
        try {
            const tickers = poss.map(p => p.ticker);
            const r = await compare(tickers);
            const byTicker = Object.fromEntries((r.results || []).map(x => [x.ticker, x]));
            setRows(poss.map(p => ({ position: p, ...enrichRow(byTicker[p.ticker], p) })));
        } catch { toast.error("Error al cargar"); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const ps = getPortfolio();
        setPositions(ps);
        load(ps);
        const onChange = () => {
            const next = getPortfolio();
            setPositions(next);
            load(next);
        };
        window.addEventListener("vs:portfolio-changed", onChange);
        return () => window.removeEventListener("vs:portfolio-changed", onChange);
    }, []);

    const handleSave = (pos) => {
        upsertPosition(pos);
        setShowAdd(false);
        setEditing(null);
        toast.success(t("common.save"));
    };

    const handleImport = (rows) => {
        for (const r of rows) upsertPosition({ ticker: r.ticker, shares: r.shares, alert_enabled: true });
        setShowImport(false);
        toast.success(`${rows.length} posición${rows.length === 1 ? "" : "es"} importada${rows.length === 1 ? "" : "s"}`);
    };

    const [confirmDel, setConfirmDel] = useState(null);
    const handleRemove = (ticker) => setConfirmDel(ticker);
    const doRemove = () => {
        const t = confirmDel;
        if (!t) return;
        removePosition(t);
        setConfirmDel(null);
        toast("Eliminada de Nivel 1");
    };

    const toggleAlert = (ticker, next) => {
        setPositionAlert(ticker, next);
        toast.success(next ? t("alerts.row_on") : t("alerts.row_off"));
    };

    const activeAlertCount = positions.filter(p => p.alert_enabled).length;
    const toggleAllAlerts = (next) => {
        if (!user) { toast.message(t("alerts.requires_login")); return; }
        if (positions.length === 0) return;
        setAllPositionAlerts(next);
        toast.success(next ? "Alertas activadas en todas las posiciones" : "Alertas desactivadas en todas las posiciones");
    };

    const useDisplay = displayCur && displayCur !== "NATIVE";
    const convToDisplay = (v, fromCur) => useDisplay ? fxConvert(v, fromCur) : v;
    const displayCurFor = (nativeCur) => useDisplay ? displayCur : nativeCur;

    // Positions with shares AND a recorded buy price count as "complete"; the rest are
    // shown as tracking-only rows.
    let completeCount = 0;
    for (const r of rows) {
        const p = r.position || {};
        if (p.shares && p.buy_price) completeCount += 1;
    }
    const trackedOnly = rows.length - completeCount;

    // Portfolio composition (donuts): current market value per holding in a common
    // currency (see useDonutFx — never mixes currencies silently), grouped by ticker
    // and by sector. Independent of buy_price (works even for tracking-only positions).
    const holdingsMap = {};
    const sectorMap = {};
    const sectorCompaniesMap = {};  // sector -> [{ key: ticker, label: ticker, value }]
    // Same held companies, weighted by market cap instead of invested value — a
    // separate classification requested alongside (not replacing) the value-based one.
    const mcapMap = {};
    const mcapSectorMap = {};
    const mcapSectorCompaniesMap = {};
    for (const r of rows) {
        const p = r.position || {};
        if (!p.shares || p.shares <= 0 || r.current_price == null) continue;
        const cur = r.currency || p.buy_currency || "USD";
        const val = toDonut(r.current_price * p.shares, cur);
        if (val == null || val <= 0) continue;
        holdingsMap[p.ticker] = val;
        const sector = r.sector || "Sin clasificar";
        sectorMap[sector] = (sectorMap[sector] || 0) + val;
        if (!sectorCompaniesMap[sector]) sectorCompaniesMap[sector] = [];
        sectorCompaniesMap[sector].push({ key: p.ticker, label: p.ticker, value: val });

        if (r.market_cap == null) continue;
        const mcapVal = toDonut(r.market_cap, cur);
        if (mcapVal == null || mcapVal <= 0) continue;
        mcapMap[p.ticker] = mcapVal;
        mcapSectorMap[sector] = (mcapSectorMap[sector] || 0) + mcapVal;
        if (!mcapSectorCompaniesMap[sector]) mcapSectorCompaniesMap[sector] = [];
        mcapSectorCompaniesMap[sector].push({ key: p.ticker, label: p.ticker, value: mcapVal });
    }
    const holdings = Object.entries(holdingsMap).map(([ticker, value]) => ({ key: ticker, label: ticker, value }));
    const sectorHoldings = Object.entries(sectorMap).map(([sector, value]) => ({
        key: sector, label: sector, value, companies: sectorCompaniesMap[sector],
    }));
    const totalHoldingsValue = holdings.reduce((s, h) => s + h.value, 0);
    const mcapHoldings = Object.entries(mcapMap).map(([ticker, value]) => ({ key: ticker, label: ticker, value }));
    const mcapSectorHoldings = Object.entries(mcapSectorMap).map(([sector, value]) => ({
        key: sector, label: sector, value, companies: mcapSectorCompaniesMap[sector],
    }));

    const onSort = (key) => setSort((prev) => nextSort(prev, key, PF_NUMERIC_KEYS));
    const sortVal = (key, r) => {
        const p = r.position || {};
        const buyCur = p.buy_currency || r.currency || "USD";
        switch (key) {
            case "ticker": return p.ticker;
            case "sector": return r.sector || "Sin clasificar";
            case "shares": return p.shares;
            case "price": return convToDisplay(r.current_price, r.currency || buyCur);
            case "mcap": return convToDisplay(r.market_cap, r.currency || buyCur);
            case "now": return p.shares ? convToDisplay((r.current_price || 0) * p.shares, r.currency || buyCur) : null;
            case "pfpct": { const hv = holdingsMap[p.ticker]; return (hv != null && totalHoldingsValue > 0) ? (hv / totalHoldingsValue) * 100 : null; }
            case "rc": return r.custom_ratios?.ratio_compra_pct;
            case "rv": return r.custom_ratios?.ratio_venta_pct;
            case "score": return qual[p.ticker]?.score;
            case "tam": return qual[p.ticker]?.tam;
            case "kpi": return qual[p.ticker]?.kpi_coef;
            case "earnings": { const info = nextEarningsInfo(r.next_earnings_date); return info ? info.days : null; }
            default: return null;
        }
    };
    const [filters, setFilters] = useLevelFilters("vs.levelFilters.portfolio");
    const sectorOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.sector || "Sin clasificar")))
        .sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" })), [rows]);
    const sortedRows = useMemo(() => {
        const sorted = !sort ? rows : [...rows].sort(makeSorter((r) => sortVal(sort.key, r), sort.dir));
        if (!levelFiltersActive(filters)) return sorted;
        return sorted.filter((r) => {
            const p = r.position || {};
            return !r.error && passesLevelFilters({
                ticker: p.ticker, name: r.name, sector: r.sector, mode: p.mode,
                rc: r.custom_ratios?.ratio_compra_pct, rv: r.custom_ratios?.ratio_venta_pct,
                score: qual[p.ticker]?.score, tam: qual[p.ticker]?.tam, kpi: qual[p.ticker]?.kpi_coef,
            }, filters);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, sort, displayCur, qual, holdingsMap, totalHoldingsValue, filters]);

    return (
        <div data-testid="portfolio-page">
            <div className="flex justify-between items-end mb-6 gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="overline text-[#B32A22]">{t("portfolio.tag")}</span>
                        {positions.length > 0 && (
                            <button
                                onClick={toggleHideMoney}
                                className="inline-flex items-center gap-1.5 text-[11px] font-mono border border-black px-2 py-0.5 hover:bg-black hover:text-white transition-colors"
                                data-testid="portfolio-hide-toggle"
                                title={hideMoney ? "Mostrar cartera" : "Ocultar cartera"}
                            >
                                {hideMoney ? <EyeOff size={13} /> : <Eye size={13} />}
                                {hideMoney ? "Mostrar cartera" : "Ocultar cartera"}
                            </button>
                        )}
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">{t("portfolio.title")}</h1>
                    {positions.length > 0 && (
                        <div className="text-xs text-[#4A4A4A] font-mono mt-1" data-testid="portfolio-count">
                            {t("portfolio.count_positions", { n: positions.length })}
                            {trackedOnly > 0 && <span className="opacity-70"> · {t("portfolio.count_with_data", { n: completeCount })} · {t("portfolio.count_tracked", { n: trackedOnly })}</span>}
                        </div>
                    )}
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                    {positions.length > 0 && (
                        <button
                            onClick={scrollToDonuts}
                            className="btn-ghost inline-flex items-center gap-1 mr-3"
                            data-testid="portfolio-goto-charts"
                        >
                            <PieChart size={14} /> Ir a gráfico
                        </button>
                    )}
                    <ViewToggle view={view} onChange={changeView} testid="portfolio-view-toggle" />
                    <button onClick={() => setShowImport(true)} className="btn-ghost inline-flex items-center gap-1" data-testid="portfolio-import-open">
                        <Upload size={14} /> Importar cartera
                    </button>
                    <button onClick={() => { setEditing(null); setShowAdd(true); }} className="btn-primary inline-flex items-center gap-1" data-testid="portfolio-add">
                        <Plus size={14} /> {t("portfolio.add_position")}
                    </button>
                </div>
            </div>

            {!user && (
                <div className="border border-[#052049] bg-white p-4 mb-6 text-sm" data-testid="portfolio-login-prompt">
                    <div className="overline text-[#052049] mb-1">{t("watchlist.login_prompt_tag")}</div>
                    <div className="text-[#4A4A4A]">{t("watchlist.login_prompt_text")}</div>
                </div>
            )}

            {positions.length > 0 && (
                <LevelFilters filters={filters} setFilters={setFilters} sectorOptions={sectorOptions}
                              shown={sortedRows.length} total={rows.length} testid="portfolio-filters" />
            )}

            {trackedOnly > 0 && (
                <div className="text-xs font-mono text-[#4A4A4A] mb-4" data-testid="portfolio-tracked-only-note">
                    {trackedOnly} {trackedOnly === 1 ? "posición sin datos" : "posiciones sin datos"} de acciones/precio (sólo seguimiento). Edita la fila para completar.
                </div>
            )}

            {!positions.length ? (
                <div className="border border-black bg-white p-12 text-center" data-testid="portfolio-empty">
                    <div className="font-serif text-3xl mb-2">{t("portfolio.empty_title")}</div>
                    <div className="text-sm text-[#4A4A4A] mb-6 max-w-md mx-auto">{t("portfolio.empty_sub")}</div>
                    <button onClick={() => setShowAdd(true)} className="btn-primary">{t("portfolio.add_position")}</button>
                </div>
            ) : view === "cards" ? (
                <>
                <CardSort options={PF_SORT_OPTIONS} sort={sort} onChange={setSort} testid="portfolio-card-sort" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="portfolio-cards">
                    {loading && <div className="col-span-full text-center py-8 font-mono text-[#4A4A4A]">{t("common.loading")}</div>}
                    {!loading && sortedRows.map((r) => {
                        const p = r.position;
                        if (r.error) return (
                            <div key={p.ticker} className="border border-[#B32A22]/40 bg-white p-3 flex items-center justify-between" data-testid={`portfolio-card-${p.ticker}`}>
                                <span className="font-mono font-bold">{p.ticker}</span>
                                <span className="text-[#B32A22] text-xs">{r.error}</span>
                                <button onClick={() => handleRemove(p.ticker)} className="text-[#B32A22]" data-testid={`remove-${p.ticker}`}><Trash2 size={12} /></button>
                            </div>
                        );
                        const buyCur = p.buy_currency || r.currency || "USD";
                        const showCur = displayCurFor(r.currency || buyCur);
                        const cr = r.custom_ratios || {};
                        const q = qual[p.ticker];
                        return (
                            <CompanyCard
                                key={p.ticker}
                                testid={`portfolio-card-${p.ticker}`}
                                ticker={p.ticker}
                                name={r.name}
                                price={convToDisplay(r.current_price, r.currency || buyCur)}
                                priceCur={showCur}
                                mcap={convToDisplay(r.market_cap, r.currency || buyCur)}
                                rc={cr.ratio_compra_pct}
                                rv={cr.ratio_venta_pct}
                                score={q?.score}
                                tam={q?.tam}
                                kpi={q?.kpi_coef}
                                nextEarnings={r.next_earnings_date}
                                badges={p.mode === "manual" && <span className="overline px-1.5 py-0.5 border border-[#1D7044] text-[#1D7044] text-[9px]" data-testid={`manual-tag-${p.ticker}`}>MANUAL</span>}
                                actions={<>
                                    <AlertToggle enabled={!!p.alert_enabled} onChange={(v) => toggleAlert(p.ticker, v)} testid={p.ticker} />
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => { setEditing(p); setShowAdd(true); }} className="text-[#4A4A4A] hover:text-black text-[11px] underline" data-testid={`edit-${p.ticker}`}>Editar</button>
                                        <button onClick={() => handleRemove(p.ticker)} className="text-[#B32A22] hover:text-black" data-testid={`remove-${p.ticker}`}><Trash2 size={13} /></button>
                                    </div>
                                </>}
                            />
                        );
                    })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" ref={donutRef}>
                    <PortfolioDonut items={holdings} currency={donutCur} blur={hideMoney} testid="portfolio-donut" title="Composición por empresa" columns={2} linkTickers />
                    <PortfolioDonut items={sectorHoldings} currency={donutCur} blur={hideMoney} testid="portfolio-donut-sector" title="Composición por sector" />
                    <PortfolioDonut items={mcapHoldings} currency={donutCur} testid="portfolio-donut-mcap" title="Capitalización por empresa" columns={2} linkTickers totalCompact showValue />
                    <PortfolioDonut items={mcapSectorHoldings} currency={donutCur} testid="portfolio-donut-mcap-sector" title="Capitalización por sector" totalCompact showValue />
                </div>
                </>
            ) : (
                <>
                <div className="border border-black bg-white overflow-x-auto" data-testid="portfolio-table">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-black">
                                <SortableTh label={t("watchlist.col_ticker")} sortKey="ticker" sort={sort} onSort={onSort} align="left" className="sticky left-0 z-20 bg-white" />
                                <SortableTh label="Sector" sortKey="sector" sort={sort} onSort={onSort} align="left" />
                                <th className="overline text-center px-2 py-2">{t("watchlist.col_mode")}</th>
                                <SortableTh label={t("watchlist.col_price")} sortKey="price" sort={sort} onSort={onSort} align="right" />
                                <SortableTh label={t("portfolio.col_shares")} sortKey="shares" sort={sort} onSort={onSort} align="right" />
                                <SortableTh label={t("portfolio.col_now")} sortKey="now" sort={sort} onSort={onSort} align="right" />
                                <SortableTh label={t("portfolio.col_pct")} sortKey="pfpct" sort={sort} onSort={onSort} align="right" />
                                <SortableTh label={t("watchlist.col_mcap")} sortKey="mcap" sort={sort} onSort={onSort} align="right" />
                                <th className="overline text-right px-2 py-2">Próx. result.</th>
                                <SortableTh label={t("watchlist.col_rc")} sortKey="rc" sort={sort} onSort={onSort} align="right" />
                                <SortableTh label={t("watchlist.col_rv")} sortKey="rv" sort={sort} onSort={onSort} align="right" />
                                <SortableTh label={t("metrics.score")} sortKey="score" sort={sort} onSort={onSort} align="right" tip={QUAL_TIPS.scoreCompany} />
                                <SortableTh label={t("metrics.tam")} sortKey="tam" sort={sort} onSort={onSort} align="right" tip={QUAL_TIPS.tamCompany} />
                                <SortableTh label={t("metrics.kpi")} sortKey="kpi" sort={sort} onSort={onSort} align="right" />
                                <th className="overline text-center px-2 py-2">
                                    <MasterAlertToggle
                                        total={positions.length}
                                        activeCount={activeAlertCount}
                                        onChange={toggleAllAlerts}
                                        disabled={!user}
                                        info={alertInfoText(t, "portfolio")}
                                    />
                                </th>
                                <th className="overline text-right px-2 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan="16" className="px-3 py-6 text-center text-[#4A4A4A]">{t("common.loading")}</td></tr>}
                            {!loading && sortedRows.map((r, i) => {
                                const p = r.position;
                                if (r.error) return (
                                    <tr key={p.ticker} className="border-b border-black/10">
                                        <td className="px-2 py-2 font-mono sticky left-0 z-10 bg-white">{p.ticker}</td>
                                        <td colSpan="14" className="px-2 py-2 text-[#B32A22] text-xs">{r.error}</td>
                                        <td className="px-2 py-2 text-right">
                                            <button onClick={() => handleRemove(p.ticker)} data-testid={`remove-${p.ticker}`}><Trash2 size={14} /></button>
                                        </td>
                                    </tr>
                                );
                                const buyCur = p.buy_currency || r.currency || "USD";
                                const showCur = displayCurFor(r.currency || buyCur);
                                const hasShares = p.shares != null && p.shares > 0;
                                const now = hasShares ? ((r.current_price || 0) * p.shares) : null;
                                const nowDisp = now == null ? null : convToDisplay(now, r.currency || buyCur);
                                const curPriceDisp = convToDisplay(r.current_price, r.currency || buyCur);
                                const cr = r.custom_ratios || {};
                                const isManual = p.mode === "manual";
                                const hv = holdingsMap[p.ticker];
                                const pfPct = (hv != null && totalHoldingsValue > 0) ? (hv / totalHoldingsValue) * 100 : null;
                                return (
                                    <tr key={p.ticker} className="group border-b border-black/10 hover:bg-[#F5E4D4]" data-testid={`portfolio-row-${p.ticker}`}>
                                        <td className="px-2 py-2 font-mono font-semibold sticky left-0 z-10 bg-white group-hover:bg-[#F5E4D4]">
                                            <Link to={`/company/${p.ticker}`} className="hover:underline">{p.ticker}</Link>
                                            {r.name && <div className="text-[10px] text-[#4A4A4A] font-sans mt-0.5">{r.name}</div>}
                                            {p.note && <div className="text-[10px] text-[#4A4A4A] font-sans mt-0.5 italic">{p.note}</div>}
                                        </td>
                                        <td className="px-2 py-2 text-[#4A4A4A] max-w-[9rem] truncate" title={r.sector || "Sin clasificar"} data-testid={`sector-${p.ticker}`}>{r.sector || "Sin clasificar"}</td>
                                        <td className="px-2 py-2 text-center">
                                            {isManual ? (
                                                <span className="overline px-1.5 py-0.5 border border-[#1D7044] text-[#1D7044] bg-white text-[10px]" data-testid={`mode-${p.ticker}`}>MAN</span>
                                            ) : (
                                                <span className="overline px-1.5 py-0.5 border border-black/30 text-[#4A4A4A] bg-white text-[10px]" data-testid={`mode-${p.ticker}`}>AUTO</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">{fmtPrice(curPriceDisp, showCur)}</td>
                                        <td className={`px-2 py-2 text-right font-mono ${moneyCls}`}>{hasShares ? fmtNum(p.shares) : "—"}</td>
                                        <td className={`px-2 py-2 text-right font-mono ${moneyCls}`}>{nowDisp == null ? "—" : fmtPrice(nowDisp, showCur)}</td>
                                        <td className="px-2 py-2 text-right font-mono" data-testid={`pfpct-${p.ticker}`}>{pfPct == null ? "—" : fmtPctRaw(pfPct)}</td>
                                        <td className="px-2 py-2 text-right font-mono">{fmtNum(convToDisplay(r.market_cap, r.currency || buyCur))}</td>
                                        <td className="px-2 py-2 text-right"><NextEarnings iso={r.next_earnings_date} testid={`pf-earnings-${p.ticker}`} /></td>
                                        <td className="px-2 py-2 text-right font-mono" style={{ color: ratioColor(cr.ratio_compra_pct) }}>{fmtPctSigned(cr.ratio_compra_pct)}</td>
                                        <td className="px-2 py-2 text-right font-mono" style={{ color: ratioColor(cr.ratio_venta_pct, "venta") }}>{fmtPctSigned(cr.ratio_venta_pct)}</td>
                                        {(() => { const q = qual[p.ticker]; return (<>
                                        <td className="px-2 py-2 text-right font-mono" data-testid={`score-${p.ticker}`}>{q?.score == null ? "—" : <span style={{ color: q.score >= 70 ? "#1D7044" : q.score >= 50 ? "#B8860B" : "#B32A22" }}>{Number(q.score).toFixed(1)}</span>}</td>
                                        <td className="px-2 py-2 text-right font-mono" data-testid={`tam-${p.ticker}`}>{q?.tam == null ? "—" : Number(q.tam).toFixed(2)}</td>
                                        <td className="px-2 py-2 text-right font-mono" data-testid={`kpi-${p.ticker}`}>{q?.kpi_coef == null ? "—" : <span style={{ color: q.kpi_coef > 1.05 ? "#1D7044" : q.kpi_coef < 0.95 ? "#B32A22" : "#B8860B", fontWeight: 600 }}>{Number(q.kpi_coef).toFixed(2)}</span>}</td>
                                        </>); })()}
                                        <td className="px-2 py-2 text-center">
                                            <AlertToggle enabled={!!p.alert_enabled} onChange={(v) => toggleAlert(p.ticker, v)} testid={p.ticker} />
                                        </td>
                                        <td className="px-2 py-2 text-right whitespace-nowrap">
                                            <button onClick={() => { setEditing(p); setShowAdd(true); }} className="text-[#4A4A4A] hover:text-black mr-1 text-[10px] underline" data-testid={`edit-${p.ticker}`}>Editar</button>
                                            <button onClick={() => handleRemove(p.ticker)} className="text-[#B32A22] hover:text-black" data-testid={`remove-${p.ticker}`}><Trash2 size={12} /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" ref={donutRef}>
                    <PortfolioDonut items={holdings} currency={donutCur} blur={hideMoney} testid="portfolio-donut-table" title="Composición por empresa" columns={2} linkTickers />
                    <PortfolioDonut items={sectorHoldings} currency={donutCur} blur={hideMoney} testid="portfolio-donut-table-sector" title="Composición por sector" />
                    <PortfolioDonut items={mcapHoldings} currency={donutCur} testid="portfolio-donut-table-mcap" title="Capitalización por empresa" columns={2} linkTickers totalCompact showValue />
                    <PortfolioDonut items={mcapSectorHoldings} currency={donutCur} testid="portfolio-donut-table-mcap-sector" title="Capitalización por sector" totalCompact showValue />
                </div>
                </>
            )}

            {showAdd && (
                <PositionDialog
                    initial={editing}
                    onClose={() => { setShowAdd(false); setEditing(null); }}
                    onSave={handleSave}
                />
            )}
            {showImport && (
                <ImportPortfolioDialog onClose={() => setShowImport(false)} onImport={handleImport} />
            )}
            <ConfirmDialog
                open={!!confirmDel}
                title="¿Eliminar de Nivel 1?"
                message={`Se eliminará ${confirmDel || ""} de tu cartera y el borrado se sincronizará en todos tus dispositivos.`}
                confirmLabel="Eliminar"
                onConfirm={doRemove}
                onCancel={() => setConfirmDel(null)}
                testid="portfolio-confirm-remove"
            />
        </div>
    );
}

function PositionDialog({ initial, onClose, onSave }) {
    const { t } = useI18n();
    const [ticker, setTicker] = useState(initial?.ticker || "");
    const [shares, setShares] = useState(initial?.shares == null ? "" : initial.shares);
    const [buyPrice, setBuyPrice] = useState(initial?.buy_price == null ? "" : initial.buy_price);
    const [buyCurrency, setBuyCurrency] = useState(initial?.buy_currency || "");
    const [buyDate, setBuyDate] = useState(initial?.buy_date || "");
    const [note, setNote] = useState(initial?.note || "");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!ticker.trim()) {
            toast.error("El ticker es obligatorio.");
            return;
        }
        // shares + buy_price are optional — allow saving a ticker as "tracked, no position size yet"
        const sStr = String(shares).trim().replace(",", ".");
        const pStr = String(buyPrice).trim().replace(",", ".");
        const sNum = sStr === "" ? null : parseFloat(sStr);
        const pNum = pStr === "" ? null : parseFloat(pStr);
        // If either field has text, it must parse to a positive number
        if (sStr !== "" && (isNaN(sNum) || sNum <= 0)) {
            toast.error("Acciones debe ser un número positivo (o déjalo vacío).");
            return;
        }
        if (pStr !== "" && (isNaN(pNum) || pNum <= 0)) {
            toast.error("Precio de compra debe ser un número positivo (o déjalo vacío).");
            return;
        }
        onSave({
            ticker: ticker.trim().toUpperCase(),
            shares: sNum,
            buy_price: pNum,
            buy_currency: buyCurrency.trim().toUpperCase() || null,
            buy_date: buyDate || null,
            note: note.trim() || null,
            alert_enabled: initial ? !!initial.alert_enabled : true,
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(17,17,17,0.5)" }} data-testid="position-dialog">
            <form onSubmit={handleSubmit} className="bg-white border border-black p-6 w-full max-w-md">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="overline text-[#B32A22]">{t("portfolio.tag")}</div>
                        <h2 className="font-serif text-2xl">{t("portfolio.dialog_title")}</h2>
                    </div>
                    <button type="button" onClick={onClose}><X size={16} /></button>
                </div>

                <Field label={t("portfolio.field_ticker")}>
                    {initial ? (
                        <input value={ticker} className="input-paper font-mono w-full" disabled data-testid="pos-ticker" />
                    ) : (
                        <TickerAutocomplete
                            value={ticker}
                            onChange={setTicker}
                            onPick={(r) => setTicker(r.symbol)}
                            placeholder="AAPL, NVDA, SAP.DE, SAN.MC…"
                            testid="pos-ticker"
                        />
                    )}
                </Field>
                <Field label={`${t("portfolio.field_shares")} (opcional)`}>
                    <input value={shares} onChange={(e) => setShares(e.target.value)} placeholder="Déjalo vacío si aún no quieres registrar la cantidad" className="input-paper font-mono w-full" inputMode="decimal" data-testid="pos-shares" />
                </Field>
                <Field label={t("portfolio.field_note")}>
                    <input value={note} onChange={(e) => setNote(e.target.value)} className="input-paper w-full" data-testid="pos-note" />
                </Field>

                <div className="flex justify-end gap-2 mt-4">
                    <button type="button" onClick={onClose} className="btn-ghost" data-testid="pos-cancel">{t("common.cancel")}</button>
                    <button type="submit" className="btn-primary" data-testid="pos-save">{t("common.save")}</button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mt-2">
            <span className="overline text-[#4A4A4A]">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}

// Some brokers (eToro) append a ".US" suffix to US-listed tickers (e.g. "TEM.US") that
// Yahoo Finance — the data source behind VegaValor — never uses; real Yahoo
// Finance suffixes only exist for foreign exchanges (.MC, .DE, .PA…). Safe to strip
// unconditionally since no genuine Yahoo Finance ticker ends in literal ".US".
const stripBrokerSuffix = (ticker) => ticker.replace(/\.US$/i, "");

// Bulk import for Nivel 1: paste "TICKER  ACCIONES" one per line (tab/comma/space
// separated — matches a raw copy from a spreadsheet or a broker's portfolio page).
// Picks the first alpha token as the ticker and the first positive number as the
// share count; extra columns (price, P/L, currency…) are ignored.
function parsePortfolioImport(text) {
    const lines = (text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const rows = [];
    const errors = [];
    for (const line of lines) {
        // Split on tab / 2+ spaces / comma — but not a comma used as a decimal separator
        // (digit directly on both sides, e.g. "100,5"), so European-style share counts
        // survive a plain two-field paste.
        const split = line.split(/\t|\s{2,}|,(?!\d)|(?<!\d),/).map((p) => p.trim()).filter(Boolean);
        const tokens = split.length >= 2 ? split : line.split(/\s+/).filter(Boolean);
        let ticker = null, shares = null;
        for (const raw of tokens) {
            if (!ticker && /^[A-Za-z][A-Za-z0-9.\-]{0,9}$/.test(raw)) {
                ticker = stripBrokerSuffix(raw.toUpperCase());
                continue;
            }
            if (shares == null) {
                const num = parseFloat(raw.replace(/[^0-9.,\-]/g, "").replace(",", "."));
                if (!isNaN(num) && num > 0) shares = num;
            }
        }
        if (ticker && shares != null) rows.push({ ticker, shares });
        else errors.push(line);
    }
    return { rows, errors };
}

function ImportPortfolioDialog({ onClose, onImport }) {
    const [text, setText] = useState("");
    const [removed, setRemoved] = useState(new Set());
    const { rows, errors } = useMemo(() => parsePortfolioImport(text), [text]);
    const finalRows = rows.filter((_, i) => !removed.has(i));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(17,17,17,0.5)" }} data-testid="import-portfolio-dialog">
            <div className="bg-white border border-black p-6 w-full max-w-lg">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="overline text-[#B32A22]">Nivel 1</div>
                        <h2 className="font-serif text-2xl">Importar cartera</h2>
                    </div>
                    <button type="button" onClick={onClose}><X size={16} /></button>
                </div>
                <p className="text-xs text-[#4A4A4A] mb-2">
                    Pega una posición por línea — ticker y número de acciones (tabulador, coma o espacio de separador), tal cual sale al copiar de un Excel o del portfolio de tu bróker. Ejemplo:
                </p>
                <pre className="text-[10px] bg-[#F5E4D4] p-2 mb-2 whitespace-pre-wrap" data-testid="import-portfolio-example">{"NVDA\t40.5\nMU\t48.6\nAAPL\t12"}</pre>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={6}
                    placeholder={"TICKER   ACCIONES\nTICKER   ACCIONES"}
                    className="input-paper font-mono w-full text-xs"
                    data-testid="import-portfolio-textarea"
                />
                {rows.length > 0 && (
                    <div className="mt-3 border border-black/20 max-h-56 overflow-y-auto" data-testid="import-portfolio-preview">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-black/20">
                                    <th className="text-left p-1">Ticker</th>
                                    <th className="text-right p-1">Acciones</th>
                                    <th className="w-6"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => removed.has(i) ? null : (
                                    <tr key={i} className="border-b border-black/10">
                                        <td className="p-1 font-mono font-semibold">{r.ticker}</td>
                                        <td className="p-1 text-right font-mono">{r.shares}</td>
                                        <td className="p-1 text-right">
                                            <button type="button" onClick={() => setRemoved((s) => new Set(s).add(i))} className="text-[#B32A22]" data-testid={`import-remove-${i}`}>
                                                <X size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {errors.length > 0 && (
                    <div className="mt-2 text-[11px] text-[#B32A22]" data-testid="import-portfolio-errors">
                        No se pudo leer {errors.length} línea{errors.length === 1 ? "" : "s"}: {errors.slice(0, 3).join(" · ")}{errors.length > 3 ? "…" : ""}
                    </div>
                )}
                <div className="flex justify-end gap-2 mt-4">
                    <button type="button" onClick={onClose} className="btn-ghost" data-testid="import-portfolio-cancel">Cancelar</button>
                    <button
                        type="button"
                        onClick={() => finalRows.length && onImport(finalRows)}
                        disabled={!finalRows.length}
                        className="btn-primary disabled:opacity-40"
                        data-testid="import-portfolio-confirm"
                    >
                        Importar {finalRows.length || ""} posici{finalRows.length === 1 ? "ón" : "ones"}
                    </button>
                </div>
            </div>
        </div>
    );
}
